import React, { useMemo, useState, useEffect, useLayoutEffect } from "react";
import { useTranslation } from "react-i18next";
import {
  FormControl,
  FormErrorMessage,
  FormLabel,
  Flex,
  Input,
  Select,
  useColorModeValue,
  useDisclosure,
} from "@chakra-ui/react";
import DateTime from "react-datetime";
import { formatNotificationNumber, isValidNotificationNumber } from "utils/forms-utils";
import { useCustomToast } from "components/CustomToast/CustomToast";
import { FilterContainer } from "components/Tables/FilterContainer";
import { LOCALE_PT_BR } from "constants/date-time";
import { NotificationsStatus, notificationsService } from "services/notificationsService";
import { parseDateBrToISO } from "utils/utils";

const NotificationsFilter = (props) => {
    const { t } = useTranslation('common');
    const { onFilter, filterSnapshot, onClearFilters } = props;
    const { isOpen, onToggle } = useDisclosure();
    const showToast = useCustomToast();
    const [notificationNumber, setNotificationNumber] = useState("");
    const [responsiblePerson, setResponsiblePerson] = useState("");
    const [startDate, setStartDate] = useState("");
    const [endDate, setEndDate] = useState("");
    const [status, setStatus] = useState(NotificationsStatus.NEW_AND_PENDING.value);
    const [users, setUsers] = useState([]);
    const isStartDateInvalid = startDate ? startDate > new Date() : false;
    const isEndDateInvalid = startDate && endDate ? endDate <= startDate : false;
    const placeholderColor = useColorModeValue("gray.300", "gray.400");

    const situations = useMemo(() => [
        NotificationsStatus.ALL,
        NotificationsStatus.NEW_AND_PENDING,
        NotificationsStatus.PENDING,
        NotificationsStatus.SOLVED
      ], []);

    const onFilterHandler = () => {
        onFilter({
            numero: notificationNumber,
            id_usuario_responsavel: responsiblePerson,
            data_inicial: startDate ? new Date(startDate).toLocaleDateString(LOCALE_PT_BR) : "",
            data_final: endDate ? new Date(endDate).toLocaleDateString(LOCALE_PT_BR) : "",
            status: status,
        });
    }

    const onClearHandler = () => {
        onClearFilters?.();
    };

    const onChangeNotificationNumber = (e) => {
        const value = e.currentTarget.value;
        const formatted = formatNotificationNumber(value);
        setNotificationNumber(formatted);
    };

    const fetchUsers = async () => {
        try {
          const response = await notificationsService.listUsers();
          const userList = response?._embedded?.usuario_list || [];
    
          const users = userList.map(user => ({
            ...user,
            username: user.pessoa?.nome || user.login,
          }));
    
          setUsers(users);
        } catch (err) {
          showToast({ title: t(err), status: "error" });
        }
    };

    useEffect(() => {
        fetchUsers();
    }, []);

    useLayoutEffect(() => {
        if (filterSnapshot == null) {
            setNotificationNumber("");
            setResponsiblePerson("");
            setStartDate("");
            setEndDate("");
            setStatus(NotificationsStatus.NEW_AND_PENDING.value);
            return;
        }
        setNotificationNumber(filterSnapshot.numero != null ? String(filterSnapshot.numero) : "");
        setResponsiblePerson(
            filterSnapshot.id_usuario_responsavel != null ? String(filterSnapshot.id_usuario_responsavel) : ""
        );
        if (filterSnapshot.data_inicial) {
            const d = parseDateBrToISO(filterSnapshot.data_inicial);
            setStartDate(d && !isNaN(d.getTime()) ? d : "");
        } else {
            setStartDate("");
        }
        if (filterSnapshot.data_final) {
            const d = parseDateBrToISO(filterSnapshot.data_final);
            setEndDate(d && !isNaN(d.getTime()) ? d : "");
        } else {
            setEndDate("");
        }
        const st = filterSnapshot.status;
        if (st === "") {
            setStatus("");
        } else if (st != null && st !== "") {
            setStatus(String(st));
        } else {
            setStatus(NotificationsStatus.NEW_AND_PENDING.value);
        }
    }, [filterSnapshot]);

    return (
        <FilterContainer
            w='125px'
            fontSize='sm'
            fontWeight='bold'
            variant="light"
            isOpen={isOpen}
            onToggle={onToggle}
            onFilter={onFilterHandler}
            onClear={onClearHandler}
        >
            <Flex w="100%" gap="4" justify="space-between">
                <FormControl isInvalid={notificationNumber && !isValidNotificationNumber(notificationNumber)  } autoComplete="off">
                    <FormLabel>
                        {t("alerts.filters.fields.number")}
                    </FormLabel>
                    <Input
                        fontSize="sm"
                        value={notificationNumber}
                        placeholder={t("alerts.filters.placeholders.number")}
                        _placeholder={{ color: placeholderColor, fontSize: "sm" }}
                        maxLength="10"
                        onChange={onChangeNotificationNumber}
                        autoComplete="off"
                    />
                    <FormErrorMessage>
                        {t("alerts.messages.errors.invalid-number")}
                    </FormErrorMessage>
                </FormControl>
            </Flex>

            <Flex w="100%" gap="4" justify="space-between">
                <FormControl w="230px" isInvalid={isStartDateInvalid}>
                    <FormLabel>
                        {t("alerts.filters.fields.start-date")}
                    </FormLabel>
                    <DateTime
                        locale={LOCALE_PT_BR}
                        closeOnSelect
                        timeFormat={false}
                        autoComplete="off"
                        inputProps={startDate ? undefined : { value: "", placeholder: t("system.placeholders.date") }}
                        renderInput={(props) => (
                          <Input
                            {...props}
                            fontSize="sm"
                            _placeholder={{ color: placeholderColor, fontSize: "sm" }}
                          />
                        )}
                        onChange={date => setStartDate(date)} />
                    <FormErrorMessage>
                        {t("system.messages.errors.invalid-start-date")}
                    </FormErrorMessage>
                </FormControl>

                <FormControl w="230px" isInvalid={isEndDateInvalid}>
                    <FormLabel>
                        {t("alerts.filters.fields.end-date")}
                    </FormLabel>
                    <DateTime
                        locale={LOCALE_PT_BR}
                        closeOnSelect
                        autoComplete="off"
                        timeFormat={false}
                        inputProps={endDate ? undefined : { value: "", placeholder: t("system.placeholders.date") }}
                        renderInput={(props) => (
                          <Input
                            {...props}
                            fontSize="sm"
                            _placeholder={{ color: placeholderColor, fontSize: "sm" }}
                          />
                        )}
                        onChange={date => setEndDate(date)} />
                    <FormErrorMessage>
                        {t("system.messages.errors.invalid-end-date")}
                    </FormErrorMessage>
                </FormControl>
            </Flex>
            <FormControl>
                <FormLabel>
                    {t("alerts.filters.fields.situation")}
                </FormLabel>
                <Select
                    fontSize="sm"
                    id="situation"
                    value={status}
                    onChange={e => setStatus(e.currentTarget.value)}
                >
                    {situations?.map((option, index) => (
                    <option key={index} value={option.value}>
                        {t(`alerts.filters.situations.${option.labelKey}`)}
                    </option>
                    ))}
                </Select>
            </FormControl>
        </FilterContainer>
    );
};

export default NotificationsFilter;
